import React, { useState } from 'react';
import { IFederalState } from '../../Interfaces/IAemter';
import {
  federalStateLoginSchema,
  IFederalStateLogin,
} from '../../Interfaces/IFederalStateLogin';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { Add, Check, Close, Edit, VpnKey } from '@mui/icons-material';
import { IUser } from '../../Interfaces/IUser';
import { IUserGroup } from '../../Interfaces/IUserGroup';
import { FedearlStateLoginRow } from './FedearlStateLoginRow';
import { EditFederalStateLogin } from './EditFederalStateLogin';
import { ErstanmeldungDialog } from './ErstanmeldungDialog';
import { useAlert } from '../../context/AlertContext';
import { useCreateFedearalStateLogin } from '../../hooks/reactQuerys/FederalStateLoginQueries';

interface IProps {
  federalState: IFederalState;
  federalStates: IFederalState[];
  federalStateLogins: IFederalStateLogin[];
  users: IUser[];
  userGroups: IUserGroup[];
  refetchFederalStateLogins: Function;
}

export const FederalStateRow: React.FC<IProps> = ({
  federalState,
  federalStates,
  federalStateLogins,
  users,
  userGroups,
  refetchFederalStateLogins,
}) => {
  const { showAlert } = useAlert();
  //
  const { createFederalStateLogin, isPendingCreateFederalStateLogin } =
    useCreateFedearalStateLogin();

  //
  const isPending = isPendingCreateFederalStateLogin;
  //
  const [isEditEnable, setIsEditEnable] = useState(false);
  //
  const [normaleLogin, setNormalLogin] = useState(
    federalStateLoginSchema.parse({})
  );
  const [stepOneLogin, setStepOneLogin] = useState(
    federalStateLoginSchema.parse({ isFirstStage: true })
  );

  const ownLogins = federalStateLogins.filter(
    login => login.idFederalState === federalState.idFederalState
  );
  const erstanmeldungen = ownLogins.filter(login => login.isFirstStage == true);

  const [isOpenNew, setIsOpenNew] = useState(false);
  const [erstDialog, setErstDialog] = useState<{
    open: boolean;
    existing?: IFederalStateLogin;
  }>({ open: false });

  const handleAddNew = () => {
    setNormalLogin(
      federalStateLoginSchema.parse({
        idFederalState: federalState.idFederalState,
      })
    );
    setStepOneLogin(
      federalStateLoginSchema.parse({
        idFederalState: federalState.idFederalState,
        isFirstStage: true,
      })
    );
    setIsOpenNew(true);
  };

  const handleCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      // Erstzugang wird separat angelegt und im Dialog nur ausgewählt — hier
      // wird nur noch der Zugang selbst gespeichert.
      const uploadObject = federalStateLoginSchema.parse(normaleLogin);
      await createFederalStateLogin(uploadObject);
      showAlert({
        text: 'Erfolgreich gespeichert!',
        severity: 'success',
      });
      setIsOpenNew(false);
      refetchFederalStateLogins();
    } catch (error) {
      console.error(error);
      showAlert({
        text: 'Es ist ein Fehler aufgetretten!',
        severity: 'error',
      });
    }
  };

  return (
    <>
      {isOpenNew && (
        <Dialog
          open
          onClose={() => setIsOpenNew(false)}
          maxWidth='xs'
          fullWidth
        >
          <DialogTitle>Neuer Login</DialogTitle>
          <form onSubmit={handleCreate}>
            <DialogContent>
              <EditFederalStateLogin
                federalState={federalState}
                normalLogin={normaleLogin}
                setNormalLogin={setNormalLogin}
                stepOneLogin={stepOneLogin}
                setStepOneLogin={setStepOneLogin}
                users={users}
                userGroups={userGroups}
                federalStateLogins={federalStateLogins}
              />
            </DialogContent>
            <DialogActions>
              <Button disabled={isPending} variant='outlined'>
                Testen
              </Button>
              <Button
                disabled={isPending}
                variant='outlined'
                onClick={() => setIsOpenNew(false)}
              >
                Abbruch
              </Button>
              <Button variant='contained' disabled={isPending} type='submit'>
                {isPending ? (
                  <Box display='flex' alignItems='center' gap={1}>
                    <span>Speichern</span>
                    <CircularProgress size={20} />
                  </Box>
                ) : (
                  'Speichern'
                )}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      )}

      {erstDialog.open && (
        <ErstanmeldungDialog
          open
          onClose={() => setErstDialog({ open: false })}
          federalState={federalState}
          users={users}
          existing={erstDialog.existing}
          refetchFederalStateLogins={refetchFederalStateLogins}
        />
      )}

      <TableRow>
        <TableCell>{federalState.FederalState}</TableCell>
        <TableCell>
          {federalState.isEnabledSolumWeb ? <Check /> : <Close />}
        </TableCell>
        <TableCell>
          {ownLogins.filter(login => login.isFirstStage == false).length}
        </TableCell>
        <TableCell>
          <Collapse in={!isEditEnable}>
            <IconButton
              disabled={!federalState.isEnabledSolumWeb}
              onClick={() => setIsEditEnable(true)}
            >
              <Edit />
            </IconButton>
          </Collapse>
          <Collapse in={isEditEnable}>
            <IconButton
              disabled={!federalState.isEnabledSolumWeb}
              onClick={handleAddNew}
              title='Hinzufügen'
            >
              <Add />
            </IconButton>
            <IconButton
              disabled={!federalState.isEnabledSolumWeb}
              onClick={() => setIsEditEnable(false)}
            >
              <Close />
            </IconButton>
          </Collapse>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
          <Collapse in={isEditEnable}>
            {/* Erstanmeldungen (nur zweistufige Bundesländer) */}
            {federalState.isTwoStage && (
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: 1,
                  }}
                >
                  <b>Erstanmeldungen</b>
                  <Button
                    size='small'
                    variant='outlined'
                    startIcon={<VpnKey />}
                    onClick={() => setErstDialog({ open: true })}
                  >
                    Erstanmeldung anlegen
                  </Button>
                </Box>
                {erstanmeldungen.length === 0 ? (
                  <i>Noch keine Erstanmeldung angelegt.</i>
                ) : (
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>Notar</TableCell>
                        <TableCell>Anmeldename</TableCell>
                        <TableCell>Aktionen</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {erstanmeldungen.map(login => (
                        <TableRow key={`erst-${login.idFederalStateLogin}`}>
                          <TableCell>
                            {users.find(u => u.idUser === login.Notary_idUser)
                              ?.LastName ?? `Notar #${login.Notary_idUser}`}
                          </TableCell>
                          <TableCell>{login.Username}</TableCell>
                          <TableCell>
                            <IconButton
                              size='small'
                              onClick={() =>
                                setErstDialog({ open: true, existing: login })
                              }
                            >
                              <Edit fontSize='small' />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            )}

            {/* Zugänge */}
            <b>Zugänge</b>
            {ownLogins.filter(login => login.isFirstStage == false).length ===
            0 ? (
              <i>
                {' '}
                Keine Zugänge vorhanden. Bitte erstellen Sie über <Add /> einen
                Zugang.
              </i>
            ) : (
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Notar</TableCell>
                    <TableCell>Benutzerkennung</TableCell>
                    <TableCell>Bearbeiterkennzeichen</TableCell>
                    <TableCell>Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ownLogins
                    .filter(login => login.isFirstStage == false)
                    .map(login => (
                      <FedearlStateLoginRow
                        key={`login-${login.idFederalStateLogin}`}
                        federalStateLogin={login}
                        federalStateLogins={ownLogins}
                        federalState={federalState}
                        users={users}
                        userGroups={userGroups}
                        refetchFederalStateLogins={refetchFederalStateLogins}
                      />
                    ))}
                </TableBody>
              </Table>
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
